import { useT } from "@transifex/react";
import { useState } from "react";
import moment from "moment";
import { CoverImage } from "p6-react-shared";
import LoadingSpinner from "../../../basic/loadingSpinner/LoadingSpinner";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import UserContentCover from "../../../basic/userContentCover/UserContentCover";
import type { ClassActivity } from "../../../complex/classActivityCard/ClassActivityCardsOverview.types";
import { ClassActivityCategory, classActivityColors } from "../../../constants/ClassActivityConstants";
import { ShareModes } from "../../../enums/shareFlow";
import { resolveShareModeWithLegacy } from "../../../helpers/LegacyTest";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { useClassActivityData } from "../../../hooks/useClassActivityData";

import { RecentlyUsedBooksSpinnerWrapper } from "./StyledComponents";

export interface ExistingActivityListProps {
    onSelectActivity: (activity: ClassActivity) => void;
    isSelectionDisabled?: boolean;
}

const INITIAL_ITEMS_SHOWN = 4;

/** Fetches activities on mount; mount only while the section is expanded. */
const ExistingActivityList = (props: ExistingActivityListProps) => {
    const [showAll, setShowAll] = useState(false);

    const { activities, allDataLoaded, schoolClasses, studentTestContentWrapper } = useClassActivityData(undefined, {
        useGlobalLoadingIndicator: false,
        reuseLoadedTrainings: true,
        onlySelectableActivities: true,
    });
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();

    const t = useT();
    const t_showMore = t("show more", { _tags: "CreateTest,ExistingActivityList" });
    const t_noActivities = t("You have no activities yet.", { _tags: "CreateTest,ExistingActivityList" });
    const t_class = t("Class:", { _tags: "CreateTest,ExistingActivityList" });
    const t_vocabulary = t("Vocabulary:", { _tags: "CreateTest,ExistingActivityList" });
    const t_testLabel = t("PRACTICE & TEST", { _tags: "CreateTest,ExistingActivityList" });
    const t_testOnlyLabel = t("TEST", { _tags: "CreateTest,ExistingActivityList" });
    const t_vocabTrainingLabel = t("VOCABULARY TRAINING", { _tags: "CreateTest,ExistingActivityList" });

    const visibleActivities = showAll ? activities : activities.slice(0, INITIAL_ITEMS_SHOWN);

    const getSchoolClassName = (activity: ClassActivity): string | undefined => {
        const groupId =
            activity.category === ClassActivityCategory.TEST
                ? activity.testData.groupId
                : activity.vocabTrainingData.groupId;
        return schoolClasses.find((schoolClass) => schoolClass.token === groupId)?.name;
    };

    const getTypeLabel = (activity: ClassActivity): string => {
        if (activity.category === ClassActivityCategory.VOCAB_TRAINING) {
            return t_vocabTrainingLabel;
        }
        const sharedTestContent = studentTestContentWrapper[activity.id];
        if (sharedTestContent && resolveShareModeWithLegacy(sharedTestContent) === ShareModes.TEST) {
            return t_testOnlyLabel;
        }
        return t_testLabel;
    };

    const renderCover = (activity: ClassActivity) => (
        <div className="shrink-0 flex justify-center">
            {activity.bookCover && (
                <CoverImage
                    imageId={activity.bookCover.src}
                    size={"small"}
                />
            )}
            {!activity.bookCover && activity.ownSubjectInfo && (
                <UserContentCover
                    primaryLanguage={activity.ownSubjectInfo.primaryLang}
                    secondaryLanguage={activity.ownSubjectInfo.secondaryLang || ""}
                    small
                />
            )}
        </div>
    );

    const renderClassAndDate = (activity: ClassActivity) => {
        const schoolClassName = getSchoolClassName(activity);
        const dateLabel = activity.sortDate ? moment(activity.sortDate).format("DD.MM.YYYY") : "";
        if (!schoolClassName && !dateLabel) return null;

        return (
            <span className="flex flex-wrap items-center gap-x-s gap-y-xxs text-s text-p6-grey-mid min-w-0">
                {schoolClassName && (
                    <span className="break-words min-w-0">
                        {isAboveMD ? `${t_class} ${schoolClassName}` : schoolClassName}
                        {dateLabel && (
                            // Joined to the last word without a space so the pipe can never wrap
                            // onto a line of its own; padding (not margin) because index.css
                            // resets span margins.
                            <span
                                aria-hidden="true"
                                data-testid="existing-activity-separator"
                                className="pl-s"
                            >
                                {"|"}
                            </span>
                        )}
                    </span>
                )}
                {dateLabel && <span className="break-words min-w-0">{`${t_vocabulary} ${dateLabel}`}</span>}
            </span>
        );
    };

    const renderActivityItem = (activity: ClassActivity) => {
        const colors = classActivityColors[activity.category];

        return (
            <button
                key={`${activity.category}-${activity.id}`}
                type="button"
                data-testid="existing-activity-item"
                disabled={props.isSelectionDisabled}
                onClick={() => props.onSelectActivity(activity)}
                className={`w-full flex gap-s items-center p-s rounded-medium cursor-pointer border border-solid ${colors.cardBorder} bg-white text-left transition-colors ${colors.buttonHover}`}
            >
                {renderCover(activity)}
                <div className="flex flex-col gap-xxs flex-1 min-w-0 overflow-hidden">
                    <span className={`text-s font-semibold ${colors.text}`}>{getTypeLabel(activity)}</span>
                    <span className="text-m font-bold text-p6-grey-dark break-words line-clamp-2">
                        {activity.subtitle}
                    </span>
                    {renderClassAndDate(activity)}
                </div>
            </button>
        );
    };

    if (!allDataLoaded) {
        return (
            <RecentlyUsedBooksSpinnerWrapper>
                <LoadingSpinner size={"h-l w-l"} />
            </RecentlyUsedBooksSpinnerWrapper>
        );
    }

    if (activities.length === 0) {
        return (
            <div className="p-s bg-background-light">
                <p className="text-s text-p6-grey-mid text-center">{t_noActivities}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-s p-s max-h-[400px] overflow-y-auto bg-background-light">
            <div className={`grid gap-s ${getStyleBasedOnBreakpoint(Breakpoint.MD, "grid-cols-2", "grid-cols-1")}`}>
                {visibleActivities.map(renderActivityItem)}
            </div>
            {!showAll && activities.length > INITIAL_ITEMS_SHOWN && (
                <button
                    type="button"
                    data-testid="existing-activity-show-more"
                    onClick={() => setShowAll(true)}
                    className="flex items-center justify-center gap-xxs w-full cursor-pointer bg-transparent border-none p-no"
                >
                    <span className="text-s text-p6-grey-dark">{t_showMore}</span>
                    <PhaseSixIcon
                        name="chevron-down"
                        className="text-l text-p6-grey-dark"
                    />
                </button>
            )}
        </div>
    );
};

export default ExistingActivityList;
